import { useEffect } from 'react';

// Dependency-free per-route SEO. Sets document.title and upserts the meta/link
// tags that matter for search + social previews, using plain DOM in a useEffect.
// No react-helmet — this project runs on an iCloud-synced disk where dependency
// installs are fragile, so we keep it zero-dependency.

const DEFAULT_TITLE = 'Loreto — Build your own harness';
const SITE_ORIGIN = 'https://loreto.io';

// Query for meta[attr="key"], creating it on <head> if it doesn't exist, then
// set its content. `attr` is 'name' or 'property'.
function upsertMeta(attr, key, content) {
  if (typeof document === 'undefined' || !key || !content) return;
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function upsertLink(rel, href) {
  if (typeof document === 'undefined' || !href) return;
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

export function useSeo({ title, description, canonical, ogTitle, ogDescription, image, type } = {}) {
  useEffect(() => {
    if (typeof document === 'undefined') return undefined;

    if (title) document.title = title;

    const url =
      canonical ||
      (typeof window !== 'undefined'
        ? `${SITE_ORIGIN}${window.location.pathname}`
        : SITE_ORIGIN);

    if (description) upsertMeta('name', 'description', description);
    upsertLink('canonical', url);

    upsertMeta('property', 'og:title', ogTitle || title);
    upsertMeta('property', 'og:description', ogDescription || description);
    upsertMeta('property', 'og:url', url);
    upsertMeta('property', 'og:type', type || 'website');
    if (image) upsertMeta('property', 'og:image', image);

    upsertMeta('name', 'twitter:title', ogTitle || title);
    upsertMeta('name', 'twitter:description', ogDescription || description);
    if (image) upsertMeta('name', 'twitter:image', image);

    // Route changes overwrite these tags in place; on unmount we only reset the
    // title so a page without useSeo shows the default.
    return () => {
      document.title = DEFAULT_TITLE;
    };
  }, [title, description, canonical, ogTitle, ogDescription, image, type]);
}
